import React from 'react';
import { reportError } from '../errorReporting';

/**
 * Detects the "stale deploy" family of errors: after a new build ships, the old
 * hashed JS/CSS chunks are deleted, so a cached shell that still points at them
 * fails to load a route chunk. Left uncaught, this renders a blank white page.
 */
export function isChunkLoadError(error) {
  if (!error) return false;
  const name = String(error.name || '');
  const message = String(error.message || '');
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

/** Clear the service worker + Cache Storage so a reload fetches the newest build. */
async function purgeCachesAndReload() {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    }
  } catch (e) {
    /* ignore — best effort */
  }
  try {
    if (typeof caches !== 'undefined' && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch (e) {
    /* ignore — best effort */
  }
  window.location.reload();
}

const styles = {
  wrap: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    boxSizing: 'border-box',
    background: '#faf6f2',
    color: '#3a2029',
    fontFamily:
      "'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    textAlign: 'center',
  },
  card: { maxWidth: '440px', width: '100%' },
  title: { fontSize: '1.35rem', fontWeight: 600, margin: '0 0 10px' },
  text: { fontSize: '0.98rem', lineHeight: 1.6, margin: '0 0 22px', opacity: 0.85 },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    justifyContent: 'center',
  },
  button: {
    appearance: 'none',
    border: 'none',
    cursor: 'pointer',
    background: '#5A2A39',
    color: '#fff',
    fontSize: '1rem',
    fontWeight: 500,
    padding: '12px 26px',
    borderRadius: '999px',
    minWidth: '140px',
  },
  link: {
    display: 'inline-flex',
    alignItems: 'center',
    color: '#5A2A39',
    fontSize: '1rem',
    fontWeight: 500,
    padding: '12px 20px',
    textDecoration: 'none',
  },
};

/**
 * App-wide safety net. Any uncaught render/runtime error (including a stale-chunk
 * ChunkLoadError) shows a friendly recovery screen with a hard reload instead of
 * a blank page. Uses inline styles on purpose so it works even if a stylesheet
 * chunk failed to load.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('App error boundary caught:', error, info);
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      try {
        window.gtag('event', 'exception', {
          description: String((error && error.message) || error).slice(0, 300),
          fatal: true,
        });
      } catch (e) {
        /* ignore analytics failures */
      }
    }
    // Report to server so errors are visible in Admin → System → Error log
    try {
      reportError({
        error_type: 'boundary',
        message: String((error && error.message) || error).slice(0, 2000),
        stack: (error && error.stack) ? String(error.stack).slice(0, 5000) : null,
        url: typeof window !== 'undefined' ? window.location.href : null,
        extra: {
          component_stack: info && info.componentStack
            ? String(info.componentStack).slice(0, 3000)
            : null,
        },
      });
    } catch (e) {
      /* ignore reporting failures */
    }
  }

  handleReload() {
    purgeCachesAndReload();
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const chunk = isChunkLoadError(this.state.error);
    return (
      <div style={styles.wrap} role="alert">
        <div style={styles.card}>
          <h1 style={styles.title}>
            {chunk ? 'A new version is available' : 'Something went wrong'}
          </h1>
          <p style={styles.text}>
            {chunk
              ? 'Please reload to load the latest version of the site.'
              : 'We hit an unexpected error. Reloading usually fixes it.'}
          </p>
          <div style={styles.actions}>
            <button type="button" style={styles.button} onClick={this.handleReload}>
              Reload
            </button>
            <a style={styles.link} href="/">
              Go to homepage
            </a>
          </div>
        </div>
      </div>
    );
  }
}
